/* ==========================================================================
   VAT PRICING (custom)
   Prestige's theme.js renders the raw inc-VAT price into
   .ProductMeta__PriceList--holder on every variant change. For GBP markets
   (gb / isle-of-man) we re-render the holder with the ex-VAT price as the
   headline and expose the inc-VAT price only via .incVatPrice + the
   data-inc-vat attribute (revealed by the CSS `content` technique in
   vat-pricing.css), so crawlers/bots read the ex-VAT figure. Kept in its own
   asset (not theme.js) so theme upgrades don't clobber it. Mirrors the
   server-side render in snippets/vat-money.liquid + snippets/vat-inc-badge.liquid.
   ========================================================================== */

/* --------------------------------------------------------------------------
   VAT exemption help text
   The VAT-exempt app injects <h3 id="vat-title-text"> into its form at runtime.
   Insert the team-editable help text (Theme settings → Cart → VAT exemption)
   directly after it. Uses a MutationObserver because the app renders the form
   asynchronously, and is idempotent.
   -------------------------------------------------------------------------- */
(function () {
  var html = window.theme && window.theme.vatExemptionHelpText;
  if (!html) return;

  var observer;

  // Returns true once the help text is in place (or the heading is already
  // followed by it), so the observer can stop watching.
  function inject() {
    var heading = document.getElementById('vat-title-text');
    if (!heading) return false;
    if (!(heading.nextElementSibling && heading.nextElementSibling.classList.contains('vat-exemption-help'))) {
      var el = document.createElement('div');
      el.className = 'vat-exemption-help';
      el.innerHTML = html;
      heading.insertAdjacentElement('afterend', el);
    }
    if (observer) observer.disconnect();
    return true;
  }

  // The app's form is server-rendered (present at load); the observer is a
  // fallback in case it appears late, and disconnects as soon as it injects.
  if (!(document.body && inject())) {
    observer = new MutationObserver(inject);
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }
})();

(function () {
  if (window.__vatPricingInit) return;   // guard against the script running twice
  window.__vatPricingInit = true;

  var market = document.documentElement.getAttribute('data-market');
  if (market !== 'gb' && market !== 'isle-of-man') return;

  // Shopify's standard money formatter. theme.js keeps its Currency object
  // private inside an IIFE, so we can't call Currency.formatMoney — format
  // ourselves from window.theme's money format strings.
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    function withDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      if (isNaN(number) || number == null) return '0';
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.'),
          major = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
          minor = parts[1] ? decimal + parts[1] : '';
      return major + minor;
    }
    var value = '', match = format.match(placeholder);
    switch (match && match[1]) {
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': value = withDelimiters(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = withDelimiters(cents, 0, ' '); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'"); break;
      default: value = withDelimiters(cents, 2);
    }
    return format.replace(placeholder, value);
  }

  function fmt(cents) {
    // Always use moneyFormat (no "GBP" currency code) for VAT prices.
    return formatMoney(cents, window.theme.moneyFormat);
  }

  // shop.money_format can emit <span class=money>…</span>; strip it to plain
  // text so the data-inc-vat attribute holds just the price. Escape double-quotes
  // (only) so the value can't break the attribute; leave &...; entities intact.
  function plain(cents) {
    return fmt(cents).replace(/<[^>]*>/g, '').trim().replace(/"/g, '&quot;');
  }

  // Parse a rendered money string (GBP: "£99.99 GBP", "£1,234.99") to integer
  // pence. Returns null if no number is present.
  function parseMoney(text) {
    var m = (text || '').replace(/,/g, '').match(/[0-9]+(?:\.[0-9]+)?/);
    return m ? Math.round(parseFloat(m[0]) * 100) : null;
  }

  var applying = false;

  // Re-render the holder's price as ex-VAT + the crawler-hidden inc-VAT (or the
  // incvat / giftcard variants), reading the inc-VAT figure that theme.js just
  // wrote into the DOM — so this works regardless of whether the
  // variant:changed event reaches us and needs no product JSON / variant lookup.
  // Only re-render ex-VAT when the store is showing GBP (base currency). In any
  // other currency Shopify Markets converts prices and the ex-VAT maths would
  // break the presentment conversion, so leave the localised inc price as-is.
  function isGbp() {
    var cur = (window.theme && window.theme.currency) ||
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP';
    return cur === 'GBP';
  }

  function reapply(holder) {
    if (!holder || applying) return;
    if (!isGbp()) return;                                       // non-GBP: leave localised inc price
    var mode = holder.getAttribute('data-vat-mode') || '';
    if (mode === 'giftcard') return;                            // never ex-VAT
    if (holder.querySelector('.vat--excl, .vat--incl')) return; // ours already present

    var priceEl = holder.querySelector('.ProductMeta__Price:not(.Price--compareAt), .ProductItem__Price:not(.Price--compareAt)');
    if (!priceEl) return;
    var incCents = parseMoney(priceEl.textContent);
    if (incCents == null) return;

    var compareEl = holder.querySelector('.Price--compareAt'),
        compareInc = compareEl ? parseMoney(compareEl.textContent) : null;

    var inclLabel = holder.getAttribute('data-vat-incl-label') || 'inc. VAT',
        exclLabel = holder.getAttribute('data-vat-excl-label') || 'exc. VAT',
        incVat = mode === 'incvat',
        headline = incVat ? incCents : Math.round(incCents / 1.2),
        html = '';

    // Classes must mirror the server render in snippets/product-meta.liquid so
    // the size does not change on variant selection.
    if (compareInc != null) {
      html += '<span class="ProductMeta__Price Price Price--highlight Text--subdued bold text" data-money-convertible>' + fmt(headline) + '</span>';
    } else {
      html += '<span class="ProductMeta__Price Price color-pink bold u-h3 no-margin" data-money-convertible>' + fmt(headline) + '</span>';
    }

    // Build the sale strikethrough (if any) so it sits right after the VAT
    // label and carries the matching VAT label — mirrors snippets/vat-inc-badge.
    var compareHtml = '';
    if (compareInc != null) {
      var headlineCompare = incVat ? compareInc : Math.round(compareInc / 1.2),
          compareLabel = incVat ? inclLabel : exclLabel;
      compareHtml = '&nbsp;<span class="Price Price--compareAt vat--compare Text--subdued no-margin u-h5 small" data-money-convertible>' + fmt(headlineCompare) + ' ' + compareLabel + '</span>';
    }

    if (incVat) {
      html += '<span class="vat--incl color-pink bold no-margin">&nbsp;' + inclLabel + '</span>';
      html += compareHtml;
    } else {
      html += '<span class="vat--excl color-pink bold no-margin">&nbsp;' + exclLabel + '</span>';
      html += compareHtml;
      html += '<span class="incVatPrice color-pink bold no-margin" data-inc-vat="' + plain(incCents) + ' ' + inclLabel + '"></span>';
    }

    applying = true;
    holder.innerHTML = html;
    applying = false;
  }

  // Observe the stable price container (not just the holder) with subtree, so we
  // catch both theme.js rewriting the holder's innerHTML and the holder node
  // being replaced wholesale on variant change.
  function observe(container) {
    new MutationObserver(function () {
      var holder = container.querySelector('.ProductMeta__PriceList--holder');
      if (holder) reapply(holder);
    }).observe(container, { childList: true, subtree: true });
  }

  // Second trigger, in case the observer doesn't catch the write: the theme
  // dispatches variant:changed after updating the price.
  document.addEventListener('variant:changed', function (event) {
    var t = event.target,
        section = t && t.closest ? t.closest('[data-section-type="product"]') : null,
        holder = (section || document).querySelector('.ProductMeta__PriceList--holder');
    if (holder) reapply(holder);
  });

  function init() {
    var containers = document.querySelectorAll('[data-section-type="product"] .ProductMeta__PriceList');
    for (var i = 0; i < containers.length; i++) observe(containers[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
